import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Music, Sparkles, Loader2, Download, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import LeftRail from "@/components/LeftRail";
import { fetchConfig, generateMusic, musicHistory, deleteMusic, mediaDownloadUrl, discoverMusicModels } from "@/lib/falApi";

const cls = (...a) => a.filter(Boolean).join(" ");
const LENGTH_HINTS = [
  ["", "Auto"],
  ["about 30 seconds long", "~30s"],
  ["about 1 minute long", "~1 min"],
  ["about 2 minutes long", "~2 min"],
  ["a full-length song, around 3 minutes", "Full"],
];
const IDEAS = [
  "lofi hip-hop beat, warm vinyl crackle, mellow piano, chill study vibe",
  "epic orchestral trailer, soaring strings, thunderous drums, heroic brass",
  "ambient synth pad, slow evolving textures, cinematic and dreamy",
  "upbeat funk groove, punchy bass, bright horns, danceable",
];

export default function MusicStudio() {
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState("");
  const [prompt, setPrompt] = useState("");
  const [duration, setDuration] = useState(30);
  const [lengthHint, setLengthHint] = useState("");
  const [discovering, setDiscovering] = useState(false);
  const [busy, setBusy] = useState(false);
  const [output, setOutput] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => { document.documentElement.classList.add("dark"); }, []);
  const loadHistory = useCallback(async () => { try { setHistory(await musicHistory()); } catch (e) { console.error("Failed to load music history", e); } }, []);

  const runDiscover = useCallback(async (silent) => {
    setDiscovering(true);
    try {
      const data = await discoverMusicModels();
      if (data.models) setModels(data.models);
      if (data.added?.length) {
        toast.success(`New model available: ${data.added[0].name}!`);
        setModelId(data.added[0].id);
      } else if (!silent && !data.throttled) {
        toast("You're already on the latest models.");
      }
    } catch (e) { if (!silent) toast.error("Couldn't check for new models"); }
    finally { setDiscovering(false); }
  }, []);

  useEffect(() => {
    const last = Number(localStorage.getItem("lastMusicDiscover") || 0);
    if (Date.now() - last > 24 * 3600 * 1000) {
      localStorage.setItem("lastMusicDiscover", String(Date.now()));
      runDiscover(true);
    }
  }, [runDiscover]);

  useEffect(() => {
    fetchConfig().then((c) => {
      setModels(c.music_models || []);
      if (c.music_models?.[0]) setModelId(c.music_models[0].id);
    }).catch(() => {});
    loadHistory();
  }, [loadHistory]);

  const model = models.find((m) => m.id === modelId);

  const run = async () => {
    if (!prompt.trim()) { toast.error("Describe the music you want"); return; }
    setBusy(true); setOutput(null);
    try {
      let finalPrompt = prompt.trim();
      if (!model?.has_duration && lengthHint) finalPrompt += `. Make it ${lengthHint}.`;
      const rec = await generateMusic({ model_id: modelId, prompt: finalPrompt, duration: model?.has_duration ? duration : undefined });
      setOutput(rec);
      toast.success("Track generated");
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Music generation failed");
    } finally { setBusy(false); }
  };

  const removeHistory = async (id) => { await deleteMusic(id).catch(() => {}); loadHistory(); };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex" data-testid="music-page">
      <LeftRail />
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 border-b border-zinc-800 flex items-center px-5 gap-3">
          <Music className="h-5 w-5 text-[#faff69]" />
          <h1 className="font-display font-bold text-lg tracking-tight">Music Studio</h1>
          <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.2em]">fal.ai</span>
          <button onClick={() => runDiscover(false)} disabled={discovering} data-testid="music-discover"
            className="ml-auto flex items-center gap-1.5 text-[11px] font-medium text-zinc-400 hover:text-[#faff69] border border-zinc-800 hover:border-zinc-600 rounded-md px-2.5 py-1 transition-colors disabled:opacity-50">
            {discovering ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Check for new models
          </button>
        </header>
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-2xl mx-auto space-y-5">
            <div>
              <label className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400">Model</label>
              <div className="grid sm:grid-cols-2 gap-2 mt-2">
                {models.map((m) => (
                  <button key={m.id} onClick={() => setModelId(m.id)} data-testid={`music-model-${m.id}`}
                    className={cls("text-left rounded-lg border p-3 transition-transform hover:-translate-y-0.5",
                      modelId === m.id ? "border-[#faff69] bg-[#faff69]/5" : "border-zinc-800 bg-[#121212] hover:border-zinc-700")}>
                    <div className="font-display font-semibold text-sm">{m.name}</div>
                    <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{m.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400">Describe the track</label>
              <Textarea data-testid="music-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)}
                placeholder="genre, mood, instruments, tempo…"
                className="mt-2 min-h-[110px] bg-[#0a0a0a] border-zinc-800 focus-visible:ring-[#faff69] text-sm resize-none" />
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                {IDEAS.map((s) => (
                  <button key={s} onClick={() => setPrompt(s)} data-testid="music-idea"
                    className="px-2.5 py-1 rounded-full bg-zinc-800 hover:bg-zinc-700 text-[11px] text-zinc-300 transition-colors truncate max-w-full">
                    {s.slice(0, 42)}…
                  </button>
                ))}
              </div>
            </div>

            {model?.has_duration && (
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400">Duration</span>
                  <span className="font-mono text-xs" data-testid="music-duration-value">{duration}s</span>
                </div>
                <Slider value={[duration]} min={5} max={model.max_duration || 180} step={5}
                  onValueChange={(v) => setDuration(v[0])} data-testid="music-duration" />
              </div>
            )}

            {model && !model.has_duration && (
              <div data-testid="length-hint-row">
                <span className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400">Length hint <span className="text-zinc-600 lowercase tracking-normal font-normal">(approx. — the model decides exact length)</span></span>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {LENGTH_HINTS.map(([val, label]) => (
                    <button key={label} onClick={() => setLengthHint(val)} data-testid={`length-hint-${label}`}
                      className={cls("px-3 py-1.5 rounded-md border text-xs font-medium transition-colors",
                        lengthHint === val ? "border-[#faff69] bg-[#faff69]/10 text-[#faff69]" : "border-zinc-800 text-zinc-400 hover:border-zinc-600")}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <Button onClick={run} disabled={busy} data-testid="music-generate"
              className="w-full py-6 text-base font-semibold bg-[#faff69] hover:bg-[#e6eb52] text-black">
              {busy ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Composing…</> : <><Sparkles className="h-5 w-5 mr-2" /> Generate track</>}
            </Button>

            {output && (
              <div className="rounded-lg border border-zinc-800 bg-[#121212] p-4 fade-in" data-testid="music-output">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-mono text-[#faff69] uppercase tracking-[0.15em]">{output.model_name}</span>
                  <a href={mediaDownloadUrl(output.audio_url, "fal-studio-track")} data-testid="music-download"
                    className="flex items-center gap-1.5 text-xs font-medium text-black bg-[#faff69] hover:bg-[#e6eb52] px-2.5 py-1 rounded-md transition-colors">
                    <Download className="h-3.5 w-3.5" /> Download
                  </a>
                </div>
                <audio src={output.audio_url} controls className="w-full" data-testid="music-audio" />
                <p className="text-xs text-zinc-500 mt-2 line-clamp-2">{output.prompt}</p>
              </div>
            )}

            <div className="pb-8">
              <label className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-400">Track history</label>
              {history.length ? (
                <div className="mt-3 space-y-2">
                  {history.map((h) => (
                    <div key={h.id} className="rounded-lg border border-zinc-800 bg-[#121212] p-3" data-testid={`music-history-${h.id}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] font-mono text-[#faff69] uppercase">{h.model_name}</span>
                        <div className="flex items-center gap-1">
                          <a href={mediaDownloadUrl(h.audio_url, `fal-studio-track-${h.id}`)} data-testid={`music-download-${h.id}`}
                            className="flex items-center gap-1 text-[11px] font-medium text-zinc-300 hover:text-[#faff69] hover:bg-zinc-800 px-2 py-1 rounded-md transition-colors">
                            <Download className="h-3.5 w-3.5" /> Download
                          </a>
                          <button onClick={() => removeHistory(h.id)} data-testid={`music-delete-${h.id}`} className="text-zinc-500 hover:text-[#FF3B30] p-1"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </div>
                      <audio src={h.audio_url} controls className="w-full h-9" />
                      <p className="text-[11px] text-zinc-500 mt-1.5 line-clamp-1">{h.prompt}</p>
                    </div>
                  ))}
                </div>
              ) : <p className="text-xs text-zinc-600 mt-3">No tracks yet.</p>}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
